import React from "react";
import {
    Box, Grid, Paper, Typography, Chip, Tooltip, IconButton,
    CircularProgress, TablePagination,
} from "@mui/material";
import RedeemIcon from "@mui/icons-material/Redeem";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { SECTION_COLORS, ROWS_PER_PAGE } from "../../constants";
import EmptyState from "../shared/EmptyState";
import ErrorState from "../shared/ErrorState";
import StatusChip from "../shared/StatusChip";

const BonusTab = ({
    bonusApi,          // { data, loading, error, refetch }
    pages,
    handlePageChange,
    onView,
    onEdit,
    onDelete,
    onAdd,
}) => {
    if (bonusApi.error) return <ErrorState error={bonusApi.error} onRetry={() => bonusApi.refetch()} />;
    if (bonusApi.loading) return <Box py={3} display="flex" justifyContent="center"><CircularProgress size={28} /></Box>;

    if (!Array.isArray(bonusApi.data) || bonusApi.data.length === 0)
        return <EmptyState message="Aucune définition de bonus configurée" color={SECTION_COLORS.bonus.main} />;

    const page = pages[2];
    const paginated = bonusApi.data.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);

    return (
        <Box>
            <Grid container spacing={2}>
                {paginated.map((b) => (
                    <Grid item xs={12} sm={6} md={4} key={b.id}>
                        <Paper elevation={0} sx={{
                            p: 2.5, borderRadius: 3, position: "relative", overflow: "hidden",
                            border: `1px solid ${SECTION_COLORS.bonus.main}15`,
                            background: `linear-gradient(135deg, ${SECTION_COLORS.bonus.main}06 0%, transparent 100%)`,
                            transition: "all 0.25s ease",
                            "&:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: `0 8px 25px ${SECTION_COLORS.bonus.main}12`,
                                borderColor: `${SECTION_COLORS.bonus.main}30`,
                            },
                        }}>
                            {/* Award policy badge */}
                            <Tooltip title="Politique d'attribution" placement="left">
                                <Chip
                                    label={b.award_policy}
                                    size="small"
                                    sx={{
                                        position: "absolute", top: 10, right: 10,
                                        fontWeight: 700, fontSize: 10, maxWidth: 120,
                                        bgcolor: SECTION_COLORS.bonus.light,
                                        color: SECTION_COLORS.bonus.main,
                                    }}
                                />
                            </Tooltip>

                            {/* Card header */}
                            <Box display="flex" alignItems="center" gap={1} mb={1} mr={10}>
                                <RedeemIcon sx={{ color: SECTION_COLORS.bonus.main, fontSize: 22, flexShrink: 0 }} />
                                <Typography fontWeight={700} fontSize={15} fontFamily="Montserrat, sans-serif" color="#1a1a2e" noWrap>
                                    {b.name}
                                </Typography>
                            </Box>

                            {/* Description */}
                            {b.description && (
                                <Typography fontSize={11} color="text.secondary" mb={1} noWrap sx={{ fontStyle: "italic" }}>
                                    {b.description}
                                </Typography>
                            )}

                            {/* Meta chips */}
                            <Box display="flex" gap={1} mb={1.5} flexWrap="wrap">
                                <Chip
                                    label={`Clé : ${b.bonus_key}`}
                                    size="small"
                                    sx={{ fontSize: 10, fontWeight: 600, bgcolor: "#f1f5f9", color: "#475569", maxWidth: 160 }}
                                />
                                {b.policy_params && Object.keys(b.policy_params).length > 0 && (
                                    <Chip
                                        label="Params"
                                        size="small"
                                        sx={{ fontSize: 10, fontWeight: 600, bgcolor: "#ecfdf5", color: "#059669" }}
                                    />
                                )}
                            </Box>

                            {/* Footer row */}
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <StatusChip active={b.active} />
                                <Box display="flex" gap={0.5}>
                                    <Tooltip title="Voir le détail">
                                        <IconButton size="small" onClick={() => onView(b)}
                                            sx={{
                                                color: SECTION_COLORS.bonus.main, bgcolor: `${SECTION_COLORS.bonus.main}10`,
                                                "&:hover": { bgcolor: `${SECTION_COLORS.bonus.main}20` }, width: 30, height: 30
                                            }}>
                                            <VisibilityIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Modifier">
                                        <IconButton size="small" onClick={() => onEdit(b)}
                                            sx={{ color: "#6366f1", bgcolor: "#eef2ff", "&:hover": { bgcolor: "#e0e7ff" }, width: 30, height: 30 }}>
                                            <EditIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Supprimer">
                                        <IconButton size="small" onClick={() => onDelete(b)}
                                            sx={{ color: "#DC2626", bgcolor: "#fef2f2", "&:hover": { bgcolor: "#fee2e2" }, width: 30, height: 30 }}>
                                            <DeleteIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            {bonusApi.data.length > ROWS_PER_PAGE && (
                <TablePagination component="div" count={bonusApi.data.length} page={page}
                    onPageChange={handlePageChange(2)} rowsPerPage={ROWS_PER_PAGE} rowsPerPageOptions={[ROWS_PER_PAGE]}
                    sx={{ mt: 1, borderTop: "1px solid #f0f0f0" }} />
            )}
        </Box>
    );
};

export default BonusTab;
